import { useCallback, useMemo, useState } from "react";

import {
  formatNumber, isInvalidNumberString, parseDecimal, parseInteger
} from "Utils/Number";
import { isNullOrWhiteSpace } from "Utils/String";
import { isDecimalField } from "Utils/Metadata";
import { IEditNumberProps } from "./IEditNumberProps";
import { IUseEditNumber } from "./IUseEditNumber";

export function useEditNumber(props: IEditNumberProps): IUseEditNumber {
  const {
    fieldName, precision, isRequired, item, onChange, translator
  } = props;
  const activeLocale = translator.getActiveLocale();
  const usedPrecision = precision ?? undefined;
  const [value, setValue] = useState<string>(
    formatNumber(activeLocale, item?.[fieldName] as number, usedPrecision) ?? ``
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const numericParser = useMemo(() => (isDecimalField(props) ? parseDecimal : parseInteger), []);

  const onChangeValue = useCallback(
    (_, newValue: string) => {
      const number = numericParser(activeLocale, newValue);
      onChange(fieldName, Number.isNaN(number) ? null : number);
      setValue(newValue);
    },
    [numericParser, activeLocale, onChange, fieldName]
  );

  const onGetErrorMessage = useCallback(
    (currentValue: string) => {
      if (isNullOrWhiteSpace(currentValue)) {
        return undefined;
      }

      if (isInvalidNumberString(activeLocale, currentValue)) {
        return translator.get(`generic.validate.numbersonly`, `Please enter numbers only!`);
      }

      return undefined;
    },
    [activeLocale, translator]
  );

  const onBlur = useCallback(
    (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = event?.currentTarget?.value;
      const number = numericParser(activeLocale, newValue);
      if (Number.isNaN(number)) {
        setValue(newValue);
        return;
      }

      setValue(formatNumber(activeLocale, number, usedPrecision));
    },
    [activeLocale, numericParser, usedPrecision]
  );

  let errorMessage = null;
  if (isRequired && isNullOrWhiteSpace(value)) {
    errorMessage = translator.get(`generic.missingvalue`, `This value is missing or invalid.`);
  }

  return {
    errorMessage,
    onBlur,
    onChange: onChangeValue,
    onGetErrorMessage,
    value
  };
}
